<template>
  <div class="h-full flex flex-col bg-[#f8fafc] overflow-auto">
    <!-- Item Selection Modal -->
    <IngredientSelectionModal
      v-if="modalMode === 'ingredient'"
      :isOpen="showModal"
      :ingredient="editingIngredient"
      :itemsList="categoryItems"
      @close="closeModal"
      @confirm="handleItemSelection"
    />
    <MaterialSelectionModal
      v-else
      :isOpen="showModal"
      :ingredient="editingIngredient"
      :itemsList="categoryItems"
      @close="closeModal"
      @confirm="handleItemSelection"
    />

    <!-- Controls Section: Search aligned right -->
    <div class="flex justify-end items-center mb-4">
      <input
        v-model="searchQuery"
        type="text"
        class="w-[300px] px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white"
        placeholder="Search by stock category..."
      >
    </div>

    <!-- Table: Following inventory pages style -->
    <table class="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-sm">
      <thead>
        <tr>
          <th>No.</th>
          <th @click="sortBy('stockCategory')" class="cursor-pointer">
            Stock Category <span v-if="sortKey === 'stockCategory'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
          </th>
          <th @click="sortBy('totalItems')" class="cursor-pointer">
            Total Items <span v-if="sortKey === 'totalItems'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
          </th>
          <th @click="sortBy('totalIngredients')" class="cursor-pointer">
            Total Ingredients <span v-if="sortKey === 'totalIngredients'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
          </th>
          <th @click="sortBy('totalMaterials')" class="cursor-pointer">
            Total Materials <span v-if="sortKey === 'totalMaterials'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
          </th>
          <th>Action</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(item, index) in sortedIngredients" :key="item.id" class="hover:bg-slate-100/50 transition-colors border-b">
          <td>{{ index + 1 }}</td>
          <td>{{ item.stockCategory }}</td>
          <td>{{ item.totalItems }}</td>
          <td>{{ item.totalIngredients }}</td>
          <td>
            <button
              type="button"
              class="text-blue-600 underline hover:text-blue-700 font-medium"
              @click="openMaterialSelection(item)"
            >
              {{ item.totalItems - item.totalIngredients }}
            </button>
          </td>
          <td class="actions">
            <button @click="editIngredient(item)" class="edit-btn">Edit</button>
          </td>
        </tr>
        <tr v-if="sortedIngredients.length === 0">
          <td colspan="5" style="text-align:center; color:#94a3b8; padding: 48px 15px; font-style: italic;">
            No ingredients found.
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import store from '../../../store'
import { apiFetch } from '../../../utils/api'
import IngredientSelectionModal from './IngredientSelectionModal.vue'
import MaterialSelectionModal from './MaterialSelectionModal.vue'

const currentUser = computed(() => store.getters.currentUser)
const companyId = computed(() => currentUser.value?.companyId || currentUser.value?.company_id || currentUser.value?.company?.id || null)
const branchId = computed(() => currentUser.value?.branchId || currentUser.value?.branch_id || currentUser.value?.branch?.id || null)

// Modal state
const showModal = ref(false)
const editingIngredient = ref(null)
const categoryItems = ref([])
const modalMode = ref('ingredient')

// Search functionality
const searchQuery = ref('')
const loading = ref(false)
const sortKey = ref('stockCategory')
const sortOrder = ref('asc')

// Data from API
const categories = ref([])
const allItems = ref([])
const mappedCategories = ref([])

// Filtered categories based on search
const filteredIngredients = computed(() => {
  if (!searchQuery.value) return mappedCategories.value
  return mappedCategories.value.filter(item =>
    item.stockCategory.toLowerCase().includes(searchQuery.value.toLowerCase())
  )
})

function sortBy(key) {
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = key
    sortOrder.value = 'asc'
  }
}

const sortedIngredients = computed(() => {
  const list = [...filteredIngredients.value]
  const modifier = sortOrder.value === 'asc' ? 1 : -1

  const getVal = (row) => {
    if (sortKey.value === 'totalMaterials') {
      return Number(row.totalItems || 0) - Number(row.totalIngredients || 0)
    }
    return row[sortKey.value]
  }

  list.sort((a, b) => {
    const va = getVal(a)
    const vb = getVal(b)
    const na = typeof va === 'number' ? va : (va !== null && va !== undefined && va !== '' ? Number(va) : NaN)
    const nb = typeof vb === 'number' ? vb : (vb !== null && vb !== undefined && vb !== '' ? Number(vb) : NaN)
    if (!Number.isNaN(na) && !Number.isNaN(nb)) {
      return (na - nb) * modifier
    }
    const sa = (va ?? '').toString().toLowerCase()
    const sb = (vb ?? '').toString().toLowerCase()
    if (sa < sb) return -1 * modifier
    if (sa > sb) return 1 * modifier
    return 0
  })

  return list
})

// Edit function - opens modal for chosen category
function editIngredient(item) {
  editingIngredient.value = { ...item } // Clone
  modalMode.value = 'ingredient'
  categoryItems.value = allItems.value.filter(i => {
    const iCatId = i.categoryId || i.category_id || i.category?.id;
    return iCatId === item.id;
  })
  showModal.value = true
}

function openMaterialSelection(item) {
  editingIngredient.value = { ...item }
  modalMode.value = 'material'
  categoryItems.value = allItems.value.filter(i => {
    const iCatId = i.categoryId || i.category_id || i.category?.id
    const isIngredient = i.isIngredient === 1 || i.isIngredient === '1' || i.is_ingredient === 1 || i.is_ingredient === '1'
    return iCatId === item.id && !isIngredient
  })
  showModal.value = true
}

// Modal functions
function closeModal() {
  showModal.value = false
  editingIngredient.value = null
  categoryItems.value = []
  modalMode.value = 'ingredient'
}

function refreshMappedCategories() {
  mappedCategories.value = categories.value.map(cat => {
    const catItems = allItems.value.filter(i => {
      const iCatId = i.categoryId || i.category_id || i.category?.id
      return iCatId === cat.id
    })

    return {
      id: cat.id,
      stockCategory: cat.name,
      totalItems: catItems.length,
      totalIngredients: catItems.filter(i =>
        i.isIngredient === 1 || i.isIngredient === '1' ||
        i.is_ingredient === 1 || i.is_ingredient === '1'
      ).length
    }
  })
}

function handleItemSelection(selectedItems) {
  if (modalMode.value === 'ingredient' && editingIngredient.value?.id) {
    const categoryId = editingIngredient.value.id
    const selectedSet = new Set(
      (selectedItems || []).map((i) => Number(i.id)).filter((n) => Number.isFinite(n))
    )
    allItems.value = allItems.value.map((item) => {
      const itemCatId = item.categoryId || item.category_id || item.category?.id
      if (itemCatId !== categoryId) return item
      const isSelected = selectedSet.has(item.id)
      return {
        ...item,
        isIngredient: isSelected ? 1 : 0,
        is_ingredient: isSelected ? 1 : 0,
      }
    })
    refreshMappedCategories()
  }
}

async function loadData() {
  if (!companyId.value && !branchId.value) return
  loading.value = true
  try {
    const params = new URLSearchParams()
    if (branchId.value) params.append('branchId', branchId.value)
    else if (companyId.value) params.append('companyId', companyId.value)

    const [catResponse, itemsResponse] = await Promise.all([
      apiFetch(`/categories?${params.toString()}`, { method: 'GET' }),
      apiFetch(`/items?${params.toString()}`, { method: 'GET' })
    ])

    if (catResponse.ok && itemsResponse.ok) {
        const catData = await catResponse.json().catch(() => [])
        const itemsData = await itemsResponse.json().catch(() => [])
        
        categories.value = Array.isArray(catData) ? catData : []
        categories.value = categories.value.filter(c => c.status === 1);
        categories.value.sort((a, b) => a.name.localeCompare(b.name))
        allItems.value = Array.isArray(itemsData) ? itemsData : []
        allItems.value = allItems.value.filter(i => i.status === 1);

        refreshMappedCategories()
    }

  } catch (error) {
    console.error('Error loading data:', error)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  loadData()
})
</script>

<style scoped>
table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.75rem;
  color: #1e293b;
}

th, td {
  padding: 8px 10px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
}

th {
  background: #f1f5f9;
  cursor: default;
  font-weight: 600;
}

tbody tr:hover {
  background: #f1f5f9;
}

.actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.actions button {
  background: none;
  border: none;
  color: #2563eb;
  cursor: pointer;
  text-decoration: underline;
  font-size: 0.85rem;
  padding: 0;
  display: flex;
  align-items: center;
}
</style>